import { ProcurementBatch, Order } from '../types';
import { 
  fetchProcurementBatches, 
  fetchProcurementBatchById 
} from './procurementService';
import { getUniqueProcurementBatchIds } from '../utils/procurementUtils';

/**
 * Cache for procurement batches to avoid repeated API calls
 */
class ProcurementCacheService {
  private cache = new Map<string, ProcurementBatch>();
  private cacheTimestamps = new Map<string, number>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private allBatchesCache: ProcurementBatch[] | null = null;
  private allBatchesCacheTimestamp = 0;

  /**
   * Gets a procurement batch by ID, using cache if available
   */
  async getProcurementBatch(batchId: string): Promise<ProcurementBatch | null> {
    // Check cache first
    if (this.isCacheValid(batchId)) {
      return this.cache.get(batchId) || null;
    }

    try {
      // Fetch from API
      const batch = await fetchProcurementBatchById(batchId);
      
      if (batch) {
        this.updateCache(batchId, batch);
      }
      
      return batch;
    } catch (error) {
      console.error(`Error fetching procurement batch ${batchId}:`, error);
      return null;
    }
  }

  /**
   * Gets multiple procurement batches by IDs, using cache when possible
   */
  async getProcurementBatches(batchIds: string[]): Promise<Map<string, ProcurementBatch>> {
    const result = new Map<string, ProcurementBatch>();
    const uncachedBatchIds: string[] = [];

    // Check cache for each batch
    for (const batchId of batchIds) {
      if (this.isCacheValid(batchId)) {
        const cachedBatch = this.cache.get(batchId);
        if (cachedBatch) {
          result.set(batchId, cachedBatch);
        }
      } else {
        uncachedBatchIds.push(batchId);
      }
    }

    // Fetch uncached batches
    if (uncachedBatchIds.length > 0) {
      try {
        const fetchPromises = uncachedBatchIds.map(id => this.getProcurementBatch(id));
        const fetchedBatches = await Promise.all(fetchPromises);
        
        fetchedBatches.forEach((batch, index) => {
          if (batch) {
            const batchId = uncachedBatchIds[index];
            result.set(batchId, batch);
          }
        });
      } catch (error) {
        console.error('Error fetching multiple procurement batches:', error);
      }
    }

    return result;
  }

  /**
   * Gets procurement batches needed for a list of orders
   */
  async getProcurementBatchesForOrders(orders: Order[]): Promise<Map<string, ProcurementBatch>> {
    const batchIds = getUniqueProcurementBatchIds(orders);
    return this.getProcurementBatches(batchIds);
  }

  /**
   * Gets all procurement batches, using cache if available
   */
  async getAllProcurementBatches(): Promise<ProcurementBatch[]> {
    // Check if all batches cache is valid
    if (this.isAllBatchesCacheValid()) {
      return this.allBatchesCache || [];
    }

    try {
      // Fetch all batches from API
      const batches = await fetchProcurementBatches();
      
      // Update cache
      this.allBatchesCache = batches;
      this.allBatchesCacheTimestamp = Date.now();
      
      // Also update individual batch cache
      batches.forEach(batch => {
        this.updateCache(batch.id, batch);
      });
      
      return batches;
    } catch (error) {
      console.error('Error fetching all procurement batches:', error);
      return this.allBatchesCache || [];
    }
  }

  /**
   * Invalidates cache for a specific batch
   */
  invalidateBatch(batchId: string): void {
    this.cache.delete(batchId);
    this.cacheTimestamps.delete(batchId);
    
    // If we have all batches cached, remove this specific batch from it
    if (this.allBatchesCache) {
      this.allBatchesCache = this.allBatchesCache.filter(batch => batch.id !== batchId);
    }
  }

  /**
   * Invalidates all cache
   */
  invalidateAll(): void {
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.allBatchesCache = null;
    this.allBatchesCacheTimestamp = 0;
  }

  /**
   * Updates cache when a batch is modified
   */
  updateBatchInCache(updatedBatch: ProcurementBatch): void {
    this.updateCache(updatedBatch.id, updatedBatch);
    
    // Update in all batches cache if it exists
    if (this.allBatchesCache) {
      const index = this.allBatchesCache.findIndex(batch => batch.id === updatedBatch.id);
      if (index >= 0) {
        this.allBatchesCache[index] = updatedBatch;
      } else {
        this.allBatchesCache.push(updatedBatch);
      }
    }
  }

  /**
   * Removes a batch from cache when it's deleted
   */
  removeBatchFromCache(batchId: string): void {
    this.invalidateBatch(batchId);
  }

  /**
   * Gets cache statistics for debugging
   */
  getCacheStats(): {
    individualCacheSize: number;
    allBatchesCacheSize: number;
    individualCacheEntries: string[];
    hasAllBatchesCache: boolean;
  } {
    return {
      individualCacheSize: this.cache.size,
      allBatchesCacheSize: this.allBatchesCache?.length || 0,
      individualCacheEntries: Array.from(this.cache.keys()),
      hasAllBatchesCache: !!this.allBatchesCache
    };
  }

  /**
   * Preloads procurement batches for a set of orders to improve performance
   */
  async preloadBatchesForOrders(orders: Order[]): Promise<void> {
    const batchIds = getUniqueProcurementBatchIds(orders);
    if (batchIds.length > 0) {
      // Fire and forget - load batches in background
      this.getProcurementBatches(batchIds).catch(error => {
        console.warn('Failed to preload procurement batches:', error);
      });
    }
  }

  // Private helper methods

  private isCacheValid(batchId: string): boolean {
    const timestamp = this.cacheTimestamps.get(batchId);
    if (!timestamp) return false;
    
    return (Date.now() - timestamp) < this.CACHE_DURATION;
  }

  private isAllBatchesCacheValid(): boolean {
    if (!this.allBatchesCache || this.allBatchesCacheTimestamp === 0) {
      return false;
    }
    
    return (Date.now() - this.allBatchesCacheTimestamp) < this.CACHE_DURATION;
  }

  private updateCache(batchId: string, batch: ProcurementBatch): void {
    this.cache.set(batchId, batch);
    this.cacheTimestamps.set(batchId, Date.now());
  }
}

// Create singleton instance
export const procurementCacheService = new ProcurementCacheService();

// Export convenience functions
export const getProcurementBatch = (batchId: string) => 
  procurementCacheService.getProcurementBatch(batchId);

export const getProcurementBatches = (batchIds: string[]) => 
  procurementCacheService.getProcurementBatches(batchIds);

export const getProcurementBatchesForOrders = (orders: Order[]) => 
  procurementCacheService.getProcurementBatchesForOrders(orders);

export const getAllProcurementBatches = () => 
  procurementCacheService.getAllProcurementBatches();

export const invalidateProcurementCache = () => 
  procurementCacheService.invalidateAll();

export const updateProcurementBatchInCache = (batch: ProcurementBatch) => 
  procurementCacheService.updateBatchInCache(batch);

export const removeProcurementBatchFromCache = (batchId: string) => 
  procurementCacheService.removeBatchFromCache(batchId);

export const preloadProcurementBatchesForOrders = (orders: Order[]) => 
  procurementCacheService.preloadBatchesForOrders(orders);